// src/services/BWRAService.js
import auditService from './AuditService';
import moduleFreshnessService from './ModuleFreshnessService';
import storage from './StorageAdapter';
import ServiceValidator from './ServiceValidator';
import { initialRisks, calculateRiskScore } from '../modules/BWRA/data/riskData';
import geoRiskService from './GeoRiskService';
import customerRiskService from './CustomerRiskService';
import transactionMonitoringService from './TransactionMonitoringService';
import externalModelService from './ExternalModelService';
import dataValidationService from './DataValidationService';
import entityService from './EntityService';
import concurrencyService from './ConcurrencyService';

class BWRAService {
  constructor() {
    this.RISKS_KEY = 'bwra_risks';
    this.DEMO_KEY = 'bwra_demo_mode';
    this.SCOPE_KEY = 'bwra_scope';
    this.APPETITE_KEY = 'bwra_appetite';
    this.POLICIES_KEY = 'bwra_policies';
    this.COMPLIANCE_KEY = 'bwra_compliance';
    this.VERSIONS_KEY = 'bwra_versions';
    this.CATEGORY_ANALYSIS_KEY = 'bwra_category_analysis';
    this.ANNUAL_REVIEWS_KEY = 'bwra_annual_reviews';
    this.SCOPE_RISK_LINKS_KEY = 'bwra_scope_risk_links';
    this.RISK_ACCEPTANCES_KEY = 'bwra_risk_acceptances';
    this.RISK_SCORE_HISTORY_KEY = 'bwra_risk_score_history';
    this.CONTROL_TEST_RESULTS_KEY = 'bwra_control_test_results';
    this.INTRO_BANNER_KEY = 'bwra_intro_banner_dismissed';
    this.listeners = [];
    // Migrate data from old ARB keys
    this._migrateFromARB();
    // Migrate from global keys to entity-scoped keys
    this._migrateToEntityScope();
    // Re-notify listeners when active entity changes
    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _migrateFromARB() {
    const oldRisks = storage.getJSON('arb_risks', null);
    const newRisks = storage.getJSON(this.RISKS_KEY, null);
    if (oldRisks !== null && newRisks === null) {
      storage.setJSON(this.RISKS_KEY, oldRisks);
      storage.removeItem('arb_risks');
    }
    const oldDemo = storage.getItem('arb_demo_mode');
    const newDemo = storage.getItem(this.DEMO_KEY);
    if (oldDemo !== null && newDemo === null) {
      storage.setItem(this.DEMO_KEY, oldDemo);
      storage.removeItem('arb_demo_mode');
    }
  }

  _migrateToEntityScope() {
    const migrated = storage.getItem('bwra_entity_scope_migrated');
    if (migrated === 'true') return;

    const entityId = entityService.getActiveEntityId();
    if (!entityId) return;

    const jsonKeys = [this.RISKS_KEY, this.SCOPE_KEY, this.APPETITE_KEY, this.POLICIES_KEY, this.COMPLIANCE_KEY, this.VERSIONS_KEY, this.CATEGORY_ANALYSIS_KEY, this.ANNUAL_REVIEWS_KEY, this.SCOPE_RISK_LINKS_KEY, this.RISK_ACCEPTANCES_KEY];
    const stringKeys = [this.DEMO_KEY];

    for (const key of jsonKeys) {
      const globalValue = storage.getJSON(key, null);
      const scopedKey = `${key}_${entityId}`;
      const scopedValue = storage.getJSON(scopedKey, null);
      if (globalValue !== null && scopedValue === null) {
        storage.setJSON(scopedKey, globalValue);
      }
    }

    for (const key of stringKeys) {
      const globalValue = storage.getItem(key);
      const scopedKey = `${key}_${entityId}`;
      const scopedValue = storage.getItem(scopedKey);
      if (globalValue !== null && scopedValue === null) {
        storage.setItem(scopedKey, globalValue);
      }
    }

    storage.setItem('bwra_entity_scope_migrated', 'true');
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  // ------------------------------------------------------------------
  // INFRA-180 — backend dual-write för all BWRA-state.
  //
  // Frontend localStorage är fortsatt snabb läs-cache. Backend rms_bwra_state
  // är auktoritativ källa över tid. Alla saveX-metoder anropar _dualWriteJson
  // istället för att skriva direkt till storage. Backend är fire-and-forget;
  // failures loggas men blockerar inte UX.
  // ------------------------------------------------------------------

  // Async variant: används från async save-metoder (saveRisks, saveScope, etc.)
  async _dualWriteJson(baseKey, value) {
    await storage.setJSON(this._entityKey(baseKey), value);
    this._syncStateToBackend(baseKey, value);
  }

  async _dualWriteFlag(baseKey, value) {
    await storage.setItem(this._entityKey(baseKey), String(value));
    this._syncStateToBackend(baseKey, value === true || value === 'true');
  }

  // Sync variant: används från synchronous seed-paths i getRisks()
  _dualWriteJsonSync(baseKey, value) {
    storage.setJSON(this._entityKey(baseKey), value);
    this._syncStateToBackend(baseKey, value);
  }

  _dualWriteFlagSync(baseKey, value) {
    storage.setItem(this._entityKey(baseKey), String(value));
    this._syncStateToBackend(baseKey, value === true || value === 'true');
  }

  /** Fire-and-forget backend-sync. Krash-säker. */
  _syncStateToBackend(baseKey, value) {
    try {
      const bwraApi = require('../api/bwra');
      bwraApi.putState(baseKey, value).catch((err) => {
        // Logga bara första gången per session för att inte spamma console
        if (!this._loggedSyncWarn) {
          console.warn('[BWRAService] backend-sync failed:', baseKey, err?.message);
          this._loggedSyncWarn = true;
        }
      });
    } catch (_) { /* api inte laddad ännu under bootstrap */ }
  }

  /**
   * INFRA-112: pull all BWRA-state från backend och merge in localStorage.
   * Anropas från App.js efter SSO-callback. Backend vinner vid konflikt
   * eftersom det är auktoritativ källa över multi-device-state.
   */
  async initFromBackend() {
    try {
      const bwraApi = require('../api/bwra');
      const remote = await bwraApi.getAllState();
      if (!remote || Object.keys(remote).length === 0) {
        // Backend tom — push upp lokala värden för aktiv entity
        const localKeys = [
          this.RISKS_KEY, this.SCOPE_KEY, this.APPETITE_KEY, this.POLICIES_KEY,
          this.COMPLIANCE_KEY, this.VERSIONS_KEY, this.CATEGORY_ANALYSIS_KEY,
          this.ANNUAL_REVIEWS_KEY, this.SCOPE_RISK_LINKS_KEY, this.RISK_ACCEPTANCES_KEY,
          this.RISK_SCORE_HISTORY_KEY, this.CONTROL_TEST_RESULTS_KEY,
        ];
        const upload = {};
        for (const k of localKeys) {
          const v = storage.getJSON(this._entityKey(k), null);
          if (v !== null) upload[k] = v;
        }
        const demo = storage.getItem(this._entityKey(this.DEMO_KEY));
        const intro = storage.getItem(this._entityKey(this.INTRO_BANNER_KEY));
        if (demo !== null) upload[this.DEMO_KEY] = demo === 'true';
        if (intro !== null) upload[this.INTRO_BANNER_KEY] = intro === 'true';
        if (Object.keys(upload).length > 0) {
          await bwraApi.patchState(upload);
        }
        return;
      }

      // Merge: backend vinner per nyckel
      for (const [key, value] of Object.entries(remote)) {
        if (key === this.DEMO_KEY || key === this.INTRO_BANNER_KEY) {
          storage.setItem(this._entityKey(key), String(value));
        } else {
          storage.setJSON(this._entityKey(key), value);
        }
      }
      this.notifyListeners();
    } catch (err) {
      console.debug('[BWRAService] initFromBackend skipped:', err?.message);
    }
  }

  // === RISKS ===

  getRisks() {
    const saved = storage.getJSON(this._entityKey(this.RISKS_KEY), null);
    if (saved === null) {
      const entityId = entityService.getActiveEntityId();
      // Seed demo data only for the default entity or when no entity system exists (fresh install)
      // New entities (subsidiaries, branches, etc.) start with empty data
      if (!entityId || entityId === 'ent-001') {
        this._dualWriteJsonSync(this.RISKS_KEY, initialRisks);
        this._dualWriteFlagSync(this.DEMO_KEY, true);
        this.seedDemoRiskScoreHistory(initialRisks);
        return initialRisks;
      }
      this._dualWriteJsonSync(this.RISKS_KEY, []);
      return [];
    }
    // Seed score history for existing demo data
    if (storage.getItem(this._entityKey(this.DEMO_KEY)) === 'true') {
      this.seedDemoRiskScoreHistory(saved);
    }
    return saved;
  }

  // Read risks for any entity without changing active entity context
  getRisksForEntity(entityId) {
    const key = entityId ? `${this.RISKS_KEY}_${entityId}` : this.RISKS_KEY;
    const saved = storage.getJSON(key, null);
    if (saved === null) {
      if (!entityId || entityId === 'ent-001') return initialRisks;
      return [];
    }
    return saved;
  }

  isDemoData() {
    return storage.getItem(this._entityKey(this.DEMO_KEY)) === 'true';
  }

  async dismissDemoBanner() {
    await this._dualWriteFlag(this.DEMO_KEY, false);
    this.notifyListeners();
  }

  isIntroBannerDismissed() {
    return storage.getItem(this._entityKey(this.INTRO_BANNER_KEY)) === 'true';
  }

  async dismissIntroBanner() {
    await this._dualWriteFlag(this.INTRO_BANNER_KEY, true);
    this.notifyListeners();
  }

  async startFresh() {
    await this._dualWriteJson(this.RISKS_KEY, []);
    await this._dualWriteFlag(this.DEMO_KEY, false);
    this.notifyListeners();
    auditService.log('DELETE', 'BWRA', { entity: 'AllRisks', action: 'start_fresh' });
  }

  async saveRisks(risks) {
    await this._dualWriteJson(this.RISKS_KEY, risks);
    await moduleFreshnessService.markModified('BWRA');
    this.notifyListeners();
  }

  async addRisk(risk) {
    ServiceValidator.validate(risk, {
      id: { required: true, type: 'string', minLength: 1 },
      name: { required: true, type: 'string', minLength: 1 },
    }, 'BWRAService.addRisk');
    const risks = this.getRisks();
    risks.push(risk);
    await this.saveRisks(risks);
    auditService.log('CREATE', 'BWRA', { entity: 'Risk', name: risk.name, id: risk.id });
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('newRiskAdded', { riskId: risk.id, riskName: risk.name, category: risk.category });
    } catch (_e) { /* best-effort */ }
    return risks;
  }

  async updateRisk(riskId, updatedRisk) {
    ServiceValidator.requireString(riskId, 'riskId', 'BWRAService.updateRisk');
    ServiceValidator.validate(updatedRisk, {
      name: { required: true, type: 'string', minLength: 1 },
    }, 'BWRAService.updateRisk');
    const risks = this.getRisks();
    const index = risks.findIndex(r => r.id === riskId);
    if (index >= 0) {
      risks[index] = updatedRisk;
      await this.saveRisks(risks);
      const grossScore = calculateRiskScore(updatedRisk.grossRisk.likelihood, updatedRisk.grossRisk.impact);
      const residualScore = calculateRiskScore(updatedRisk.residualRisk.likelihood, updatedRisk.residualRisk.impact);
      await this.saveRiskScoreSnapshot(riskId, grossScore, residualScore);
      auditService.log('UPDATE', 'BWRA', { entity: 'Risk', name: updatedRisk.name, id: riskId });
      await concurrencyService.recordSave('bwra_risk', riskId);
    }
    return risks;
  }

  async archiveRisk(riskId) {
    const risks = this.getRisks();
    const index = risks.findIndex(r => r.id === riskId);
    if (index >= 0) {
      risks[index] = { ...risks[index], status: 'ARCHIVED', archivedAt: new Date().toISOString() };
      await this.saveRisks(risks);
      auditService.log('UPDATE', 'BWRA', { entity: 'Risk', name: risks[index].name, id: riskId, action: 'archive' });
    }
    return risks;
  }

  async restoreRisk(riskId) {
    const risks = this.getRisks();
    const index = risks.findIndex(r => r.id === riskId);
    if (index >= 0) {
      const { archivedAt, ...rest } = risks[index];
      risks[index] = { ...rest, status: 'IDENTIFIED' };
      await this.saveRisks(risks);
      auditService.log('UPDATE', 'BWRA', { entity: 'Risk', name: risks[index].name, id: riskId, action: 'restore' });
    }
    return risks;
  }

  async deleteRisk(riskId, permanent = false) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (!permanent) {
      return await this.archiveRisk(riskId);
    }
    const filtered = risks.filter(r => r.id !== riskId);
    await this.saveRisks(filtered);
    if (risk) {
      auditService.log('DELETE', 'BWRA', { entity: 'Risk', name: risk.name, id: riskId, permanent: true });
    }
    return filtered;
  }

  // === CONTROLS (nested under risks) ===

  async addControl(riskId, control) {
    ServiceValidator.requireString(riskId, 'riskId', 'BWRAService.addControl');
    ServiceValidator.validate(control, {
      id: { required: true, type: 'string', minLength: 1 },
      name: { required: true, type: 'string', minLength: 1 },
    }, 'BWRAService.addControl');
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (risk) {
      risk.controls.push(control);
      await this.saveRisks(risks);
      auditService.log('CREATE', 'BWRA', { entity: 'Control', name: control.name, riskId });
    }
    return risks;
  }

  async updateControl(riskId, controlId, updatedControl) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (risk) {
      const idx = risk.controls.findIndex(c => c.id === controlId);
      if (idx >= 0) {
        risk.controls[idx] = updatedControl;
        await this.saveRisks(risks);
        auditService.log('UPDATE', 'BWRA', { entity: 'Control', name: updatedControl.name, riskId });
      }
    }
    return risks;
  }

  async deleteControl(riskId, controlId) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (risk) {
      const ctrl = risk.controls.find(c => c.id === controlId);
      risk.controls = risk.controls.filter(c => c.id !== controlId);
      await this.saveRisks(risks);
      if (ctrl) {
        auditService.log('DELETE', 'BWRA', { entity: 'Control', name: ctrl.name, riskId });
      }
    }
    return risks;
  }

  // Generate next risk ID (R009, R010, etc.)
  generateRiskId() {
    const risks = this.getRisks();
    const maxNum = risks.reduce((max, r) => {
      const num = parseInt(r.id.replace('R', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `R${String(maxNum + 1).padStart(3, '0')}`;
  }

  // Generate next control ID (C009, C010, etc.)
  generateControlId() {
    const risks = this.getRisks();
    const allControls = risks.flatMap(r => r.controls);
    const maxNum = allControls.reduce((max, c) => {
      const num = parseInt(c.id.replace('C', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `C${String(maxNum + 1).padStart(3, '0')}`;
  }

  // === ACTIONS (nested under risks) ===

  getActionsForRisk(riskId) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    return risk ? (risk.actions || []) : [];
  }

  // Aggregate all actions across all risks, enriched with risk info
  getAllActions() {
    const risks = this.getRisks();
    return risks.flatMap(risk =>
      (risk.actions || []).map(action => ({
        ...action,
        riskId: risk.id,
        riskName: risk.name,
        riskCategory: risk.category,
      }))
    );
  }

  async addAction(riskId, action) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (risk) {
      if (!risk.actions) risk.actions = [];
      risk.actions.push(action);
      await this.saveRisks(risks);
      auditService.log('CREATE', 'BWRA', { entity: 'Action', name: action.description?.substring(0, 50), riskId });
    }
    return risks;
  }

  async updateAction(riskId, actionId, updatedAction) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (risk) {
      if (!risk.actions) risk.actions = [];
      const idx = risk.actions.findIndex(a => a.id === actionId);
      if (idx >= 0) {
        risk.actions[idx] = updatedAction;
        await this.saveRisks(risks);
        auditService.log('UPDATE', 'BWRA', { entity: 'Action', name: updatedAction.description?.substring(0, 50), riskId });
      }
    }
    return risks;
  }

  async deleteAction(riskId, actionId) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (risk) {
      const action = (risk.actions || []).find(a => a.id === actionId);
      risk.actions = (risk.actions || []).filter(a => a.id !== actionId);
      await this.saveRisks(risks);
      if (action) {
        auditService.log('DELETE', 'BWRA', { entity: 'Action', name: action.description?.substring(0, 50), riskId });
      }
    }
    return risks;
  }

  // Generate next action ID (ACT-001, ACT-002, etc.)
  generateActionId() {
    const risks = this.getRisks();
    const allActions = risks.flatMap(r => r.actions || []);
    const maxNum = allActions.reduce((max, a) => {
      const num = parseInt(a.id.replace('ACT-', ''), 10);
      return isNaN(num) ? max : (num > max ? num : max);
    }, 0);
    return `ACT-${String(maxNum + 1).padStart(3, '0')}`;
  }

  // === AUTO-GENERATED CONTROLS (from declaredRisks in GeoRisk/CustomerRisk) ===

  // Returns virtual control objects for a specific BWRA risk based on
  // GeoRisk indexes and CustomerRisk parameters that declare they address it.
  // These controls are NOT persisted — they are derived on the fly.
  getAutoControlsForRisk(riskId) {
    const geoCoverage = geoRiskService.getDeclaredRiskCoverage();
    const custCoverage = customerRiskService.getDeclaredRiskCoverage();
    const txmonCoverage = transactionMonitoringService.getModelDeclaredRiskCoverage();
    const autoControls = [];

    const geoIndexes = geoCoverage[riskId] || [];
    geoIndexes.forEach(idx => {
      autoControls.push({
        id: `AUTO-GEO-${idx.id}`,
        name: idx.name,
        type: 'MODEL',
        description: '',
        effectiveness: 3,
        linkedModule: 'GeoRisk',
        linkedModelId: idx.id,
        isAutoGenerated: true,
        sourceModule: 'GeoRisk',
        sourceStatus: idx.status,
      });
    });

    const custParams = custCoverage[riskId] || [];
    custParams.forEach(param => {
      autoControls.push({
        id: `AUTO-CUST-${param.id}`,
        name: param.name,
        type: 'MODEL',
        description: param.description || '',
        effectiveness: 3,
        linkedModule: 'CustomerRisk',
        linkedModelId: null,
        isAutoGenerated: true,
        sourceModule: 'CustomerRisk',
      });
    });

    const txmonModels = txmonCoverage[riskId] || [];
    txmonModels.forEach(model => {
      autoControls.push({
        id: `AUTO-TXMON-${model.id}`,
        name: model.name,
        type: 'MODEL',
        description: '',
        effectiveness: 3,
        linkedModule: 'TransactionMonitoring',
        linkedModelId: model.id,
        isAutoGenerated: true,
        sourceModule: 'TransactionMonitoring',
        sourceStatus: model.status,
      });
    });

    // Also include TxMon rules that declare they address this risk
    const txmonRuleCoverage = transactionMonitoringService.getDeclaredRiskCoverage();
    const txmonRules = txmonRuleCoverage[riskId] || [];
    txmonRules.forEach(rule => {
      // Avoid duplicates if already added via model coverage
      if (!autoControls.find(c => c.id === `AUTO-TXRULE-${rule.id}`)) {
        autoControls.push({
          id: `AUTO-TXRULE-${rule.id}`,
          name: rule.name,
          type: 'MODEL',
          description: '',
          effectiveness: 3,
          linkedModule: 'TransactionMonitoring',
          linkedModelId: rule.id,
          isAutoGenerated: true,
          sourceModule: 'TransactionMonitoring',
          isRule: true,
        });
      }
    });

    // External "paper" models (vendor PEP screens, internal proprietary tools, etc.)
    const extCoverage = externalModelService.getDeclaredRiskCoverage();
    const extModels = extCoverage[riskId] || [];
    extModels.forEach(m => {
      autoControls.push({
        id: `AUTO-EXT-${m.id}`,
        name: m.name,
        type: 'MODEL',
        description: m.description || '',
        effectiveness: m.effectiveness || 3,
        linkedModule: 'External',
        linkedModelId: m.id,
        isAutoGenerated: true,
        sourceModule: 'External',
        sourceStatus: m.status,
        vendor: m.vendor,
      });
    });

    return autoControls;
  }

  // Returns all auto-controls across all risks, each enriched with riskId/riskName/riskCategory
  getAllAutoControls(risks) {
    return risks.flatMap(risk =>
      this.getAutoControlsForRisk(risk.id).map(ctrl => ({
        ...ctrl,
        riskId: risk.id,
        riskName: risk.name,
        riskCategory: risk.category,
      }))
    );
  }

  // === MODEL VALIDATION ===

  // Get validation status of a linked model (optionally a specific model)
  getModelScore(linkedModule, linkedModelId) {
    if (linkedModule === 'GeoRisk') {
      // If a specific index is linked, validate that index
      if (linkedModelId) {
        const allIndexes = geoRiskService.getIndexes();
        const idx = allIndexes.find(i => i.id === linkedModelId);
        if (!idx) {
          return { score: 0, status: 'NO_DATA', detail: 'Index not found', linkedModelId };
        }
        const data = geoRiskService.getCountryData(idx.id);
        const hasData = data && data.length > 0;
        const countryCount = hasData ? data.length : 0;

        let score = 30; // Index exists
        if (idx.status === 'ACTIVE') score += 20;
        if (hasData) score += 30;
        if (countryCount >= 10) score += 20;
        score = Math.min(100, score);

        // Check for actual validation results
        const valSummary = dataValidationService.getLatestValidationSummary('georisk');
        if (valSummary) {
          // Boost score based on actual validation accuracy
          score = Math.min(100, score + Math.round(valSummary.accuracy * 0.1));
        }

        return {
          score,
          status: hasData ? 'VALIDATED' : 'PARTIAL',
          detail: `${idx.name}: ${countryCount} countries` +
            (valSummary ? ` | ${valSummary.accuracy}% accuracy` : ''),
          linkedModelId,
          indexName: idx.name,
          indexType: idx.config?.type || 'UNKNOWN',
          indexStatus: idx.status,
          countryCount,
          validationAccuracy: valSummary?.accuracy || null,
          highRiskCaptureRate: valSummary?.highRiskCaptureRate || null,
          validationTimestamp: valSummary?.timestamp || null,
        };
      }

      // Fallback: module-level check (no specific index linked)
      const indexes = geoRiskService.getIndexes().filter(i => i.status === 'ACTIVE');
      if (indexes.length === 0) {
        return { score: 0, status: 'NO_DATA', detail: '0 active indexes' };
      }
      let withData = 0;
      indexes.forEach(idx => {
        const data = geoRiskService.getCountryData(idx.id);
        if (data && data.length > 0) withData++;
      });
      const coverage = withData / indexes.length;
      return {
        score: Math.round(coverage * 100),
        status: coverage > 0.5 ? 'VALIDATED' : coverage > 0 ? 'PARTIAL' : 'NO_DATA',
        detail: `${withData}/${indexes.length} indexes with data`,
        activeIndexes: indexes.length,
        indexesWithData: withData
      };
    }

    if (linkedModule === 'CustomerRisk') {
      const allModels = customerRiskService.getSavedModels();
      const params = customerRiskService.getParameters();

      // If a specific model is linked, use its data
      if (linkedModelId) {
        const model = allModels.find(m => m.name === linkedModelId);
        if (!model) {
          return { score: 0, status: 'NO_DATA', detail: `Model "${linkedModelId}" not found`, linkedModelId };
        }
        const modelParams = model.parameters?.length || 0;
        const scenarios = customerRiskService.getScenarios(linkedModelId);
        const hasScenarios = scenarios.length > 0;
        const hasParams = modelParams > 0;

        let score = 30; // Base: model exists
        if (hasParams) score += 20; // Has parameters

        let latestScenario = null;
        if (hasScenarios) {
          // Sort by date to find latest scenario
          const sorted = [...scenarios].sort((a, b) =>
            new Date(b.savedAt) - new Date(a.savedAt)
          );
          latestScenario = sorted[0];

          score += 20; // Has been simulated at least once

          // Latest scenario produced a meaningful result (model is working)
          if (latestScenario.totalScore > 0) score += 15;

          // Multiple scenarios provide broader validation coverage
          if (scenarios.length >= 2) score += 15;

          score = Math.min(100, score);
        }

        // Check for actual validation results for this specific model
        const valSummary = dataValidationService.getModelValidationSummary('customerrisk', linkedModelId);
        if (valSummary) {
          score = Math.min(100, score + Math.round(valSummary.accuracy * 0.1));
        }

        return {
          score,
          status: hasScenarios ? 'VALIDATED' : 'PARTIAL',
          detail: hasScenarios
            ? `${model.name} v${model.version}: ${modelParams} params, ${scenarios.length} scenarios (latest: ${latestScenario.totalScore})` +
              (valSummary ? ` | ${valSummary.accuracy}% accuracy` : '')
            : `${model.name} v${model.version}: ${modelParams} params, 0 scenarios`,
          linkedModelId,
          modelName: model.name,
          modelVersion: model.version,
          parameterCount: modelParams,
          scenarioCount: scenarios.length,
          latestScenarioScore: latestScenario != null ? latestScenario.totalScore : null,
          latestScenarioName: latestScenario != null ? latestScenario.name : null,
          validationAccuracy: valSummary?.accuracy || null,
          highRiskCaptureRate: valSummary?.highRiskCaptureRate || null,
          validationTimestamp: valSummary?.timestamp || null,
        };
      }

      // Fallback: module-level check (no specific model linked)
      if (allModels.length === 0) {
        return { score: 0, status: 'NO_DATA', detail: 'No saved models' };
      }
      const hasParams = params.length > 0;
      return {
        score: hasParams ? 80 : 40,
        status: hasParams ? 'VALIDATED' : 'PARTIAL',
        detail: `${allModels.length} models, ${params.length} parameters`,
        modelCount: allModels.length,
        parameterCount: params.length
      };
    }

    if (linkedModule === 'TransactionMonitoring') {
      const rules = transactionMonitoringService.getRules();
      const activeRules = rules.filter(r => r.enabled);
      const mappedRules = rules.filter(r => (r.declaredRisks || []).length > 0);

      if (linkedModelId) {
        // Check if linked ID is a rule
        const rule = transactionMonitoringService.getRule(linkedModelId);
        if (rule) {
          const hasDeclaredRisks = (rule.declaredRisks || []).length > 0;
          return {
            score: rule.enabled ? (hasDeclaredRisks ? 70 : 40) : 20,
            status: rule.enabled ? 'VALIDATED' : 'PARTIAL',
            detail: `${rule.name}: ${rule.enabled ? 'enabled' : 'disabled'}${hasDeclaredRisks ? ', mapped' : ''}`,
            linkedModelId,
            ruleName: rule.name,
            ruleEnabled: rule.enabled,
          };
        }
        // Check if it's a model
        const model = transactionMonitoringService.getModel(linkedModelId);
        if (model) {
          const linkedRulesCount = (model.linkedRules || []).length;
          let score = 30;
          if (model.status === 'active') score += 20;
          if (linkedRulesCount > 0) score += 30;
          if (linkedRulesCount >= 3) score += 20;
          return {
            score: Math.min(100, score),
            status: linkedRulesCount > 0 ? 'VALIDATED' : 'PARTIAL',
            detail: `${model.name}: ${linkedRulesCount} rules linked`,
            linkedModelId,
            modelName: model.name,
          };
        }
        return { score: 0, status: 'NO_DATA', detail: 'Rule/Model not found', linkedModelId };
      }

      if (activeRules.length === 0) {
        return { score: 0, status: 'NO_DATA', detail: '0 active rules' };
      }
      return {
        score: Math.round((mappedRules.length / activeRules.length) * 100),
        status: mappedRules.length > 0 ? 'VALIDATED' : 'PARTIAL',
        detail: `${activeRules.length} active rules, ${mappedRules.length} mapped to risks`,
        activeRules: activeRules.length,
        mappedRules: mappedRules.length,
      };
    }

    if (linkedModule === 'External') {
      if (!linkedModelId) {
        return { score: 0, status: 'NO_DATA', detail: 'No external model linked' };
      }
      const m = externalModelService.getModel(linkedModelId);
      if (!m) {
        return { score: 0, status: 'NO_DATA', detail: 'External model not found', linkedModelId };
      }
      // No automated validation possible — score reflects governance maturity
      // (manual deklaration of trust) rather than data-driven backtest.
      let score = 30;
      if (m.approvalStatus === 'submitted') score += 20;
      if (m.approvalStatus === 'approved')  score += 40;
      if (m.documentation?.links?.length)   score += 15;
      if (m.validityPeriod?.validUntil &&
          new Date(m.validityPeriod.validUntil) > new Date()) score += 15;
      score = Math.min(100, score);

      return {
        score,
        status: m.approvalStatus === 'approved' ? 'VALIDATED' : 'PARTIAL',
        detail: `${m.name} (${m.vendor || 'internal'}) — ${m.approvalStatus}`,
        linkedModelId,
        modelName: m.name,
        vendor: m.vendor,
        approvalStatus: m.approvalStatus,
        isExternal: true,
      };
    }

    if (!linkedModule) {
      return { score: 0, status: 'NOT_MODELED', detail: 'No RMS module linked — manually or system controlled' };
    }

    return { score: 0, status: 'UNKNOWN', detail: 'Unknown module' };
  }

  // Calculate residual risk from controls' effectiveness + model validation
  calculateResidualRisk(risk) {
    const grossScore = calculateRiskScore(risk.grossRisk.likelihood, risk.grossRisk.impact);

    // Merge manual controls with auto-generated controls from GeoRisk/CustomerRisk/TxMon.
    // Deduplicate: skip auto-controls for a module already covered by a manual MODEL-control.
    const autoControls = this.getAutoControlsForRisk(risk.id);
    const coveredModules = new Set(
      risk.controls.filter(c => c.linkedModule).map(c => c.linkedModule)
    );
    const filteredAuto = autoControls.filter(ac => !coveredModules.has(ac.linkedModule));
    const allControls = [...risk.controls, ...filteredAuto];

    if (allControls.length === 0) {
      return null; // No controls = no calculation possible
    }

    // Effectiveness (1-5) → reduction factor
    const reductionMap = { 1: 0.10, 2: 0.20, 3: 0.35, 4: 0.50, 5: 0.65 };

    let remaining = 1;
    const controlDetails = [];

    for (const ctrl of allControls) {
      const baseReduction = reductionMap[ctrl.effectiveness] || 0;
      let actualReduction = baseReduction;

      // Always get model info for display purposes (returns NOT_MODELED for unlinked controls)
      const modelInfo = this.getModelScore(ctrl.linkedModule, ctrl.linkedModelId);

      if (ctrl.linkedModule) {
        // Model validation scales effectiveness: 50-100% of base
        const modelFactor = modelInfo.score / 100;
        actualReduction = baseReduction * (0.5 + 0.5 * modelFactor);
      }
      // else: unlinked controls (SYSTEM/MANUAL) use full base effectiveness

      remaining *= (1 - actualReduction);
      controlDetails.push({
        controlId: ctrl.id,
        controlName: ctrl.name,
        linkedModule: ctrl.linkedModule,
        effectiveness: ctrl.effectiveness,
        baseReduction: Math.round(baseReduction * 100),
        actualReduction: Math.round(actualReduction * 100),
        modelInfo
      });
    }

    const calculatedScore = Math.max(1, Math.round(grossScore * remaining));
    const reductionPercent = Math.round((1 - remaining) * 100);

    // Map back: keep impact constant, derive likelihood
    const impact = risk.grossRisk.impact;
    const calculatedLikelihood = Math.max(1, Math.ceil(calculatedScore / impact));

    // Manual residual for comparison
    const manualScore = calculateRiskScore(risk.residualRisk.likelihood, risk.residualRisk.impact);

    return {
      score: calculatedScore,
      likelihood: calculatedLikelihood,
      impact,
      reductionPercent,
      manualScore,
      difference: manualScore - calculatedScore,
      controlDetails
    };
  }

  // === RISK APPETITE ===

  getRiskAppetite() {
    return storage.getJSON(this._entityKey(this.APPETITE_KEY), {
      statement: '',
      thresholds: {
        GEO: { acceptable: 4, elevated: 9 },
        CUST: { acceptable: 4, elevated: 9 },
        PROD: { acceptable: 4, elevated: 9 },
        TX: { acceptable: 4, elevated: 9 },
        CHAN: { acceptable: 4, elevated: 9 },
      },
      lastUpdated: null,
      updatedBy: '',
    });
  }

  async saveRiskAppetite(appetite) {
    const data = { ...appetite, lastUpdated: new Date().toISOString() };
    await this._dualWriteJson(this.APPETITE_KEY, data);
    await moduleFreshnessService.markModified('BWRA');
    this.notifyListeners();
    auditService.log('UPDATE', 'BWRA', { entity: 'RiskAppetite', action: 'save_appetite' });
  }

  // === SCOPE DOCUMENTATION ===

  getScope() {
    const scope = storage.getJSON(this._entityKey(this.SCOPE_KEY), {
      businessDescription: '',
      products: [],
      customerCategories: [],
      distributionChannels: [],
      geoExposure: { description: '', linkedIndexIds: [] },
      riskAssessmentReferences: {
        snra: { considered: false, reference: '', description: '', affectedCategories: [] },
        nra: { considered: false, authority: '', reference: '', description: '', affectedCategories: [] },
      },
      lastUpdated: null,
      updatedBy: '',
    });
    // Migration: add riskAssessmentReferences if missing (pre-BWRA-004 data)
    if (!scope.riskAssessmentReferences) {
      scope.riskAssessmentReferences = {
        snra: { considered: false, reference: '', description: '', affectedCategories: [] },
        nra: { considered: false, authority: '', reference: '', description: '', affectedCategories: [] },
      };
    }
    // Migration: convert flat string arrays to hierarchical format (BWRA-056)
    const toHierarchical = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return arr || [];
      if (typeof arr[0] === 'string') {
        return arr.filter(item => item !== undefined).map(item => ({ name: item, children: [] }));
      }
      return arr;
    };
    scope.products = toHierarchical(scope.products);
    scope.customerCategories = toHierarchical(scope.customerCategories);
    scope.distributionChannels = toHierarchical(scope.distributionChannels);
    return scope;
  }

  async saveScope(scope) {
    await this._dualWriteJson(this.SCOPE_KEY, scope);
    await moduleFreshnessService.markModified('BWRA');
    this.notifyListeners();
    auditService.log('UPDATE', 'BWRA', { entity: 'Scope', action: 'save_scope' });
  }

  // === POLICIES ===

  getPolicies() {
    return storage.getJSON(this._entityKey(this.POLICIES_KEY), []);
  }

  async savePolicies(policies) {
    await this._dualWriteJson(this.POLICIES_KEY, policies);
    this.notifyListeners();
  }

  async addPolicy(policy) {
    const policies = this.getPolicies();
    policies.push(policy);
    await this.savePolicies(policies);
    auditService.log('CREATE', 'BWRA', { entity: 'Policy', name: policy.name, id: policy.id });
    return policies;
  }

  async updatePolicy(policyId, updatedPolicy) {
    const policies = this.getPolicies();
    const index = policies.findIndex(p => p.id === policyId);
    if (index >= 0) {
      policies[index] = { ...updatedPolicy, updatedAt: new Date().toISOString() };
      await this.savePolicies(policies);
      auditService.log('UPDATE', 'BWRA', { entity: 'Policy', name: updatedPolicy.name, id: policyId });
    }
    return policies;
  }

  async deletePolicy(policyId) {
    const policies = this.getPolicies();
    const policy = policies.find(p => p.id === policyId);
    const filtered = policies.filter(p => p.id !== policyId);
    await this.savePolicies(filtered);
    if (policy) {
      auditService.log('DELETE', 'BWRA', { entity: 'Policy', name: policy.name, id: policyId });
    }
    return filtered;
  }

  generatePolicyId() {
    const policies = this.getPolicies();
    const maxNum = policies.reduce((max, p) => {
      const num = parseInt(p.id.replace('POL-', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `POL-${String(maxNum + 1).padStart(3, '0')}`;
  }

  generateProcedureId(policy) {
    const procs = policy.procedures || [];
    const maxNum = procs.reduce((max, p) => {
      const num = parseInt(p.id.replace('PROC-', ''), 10);
      return num > max ? num : max;
    }, 0);
    return `PROC-${String(maxNum + 1).padStart(3, '0')}`;
  }

  // Get risk categories that have no linked policies (gap indicator)
  getPolicyGaps() {
    const allCategories = ['GEO', 'CUST', 'PROD', 'TX', 'CHAN'];
    const policies = this.getPolicies().filter(p => p.status !== 'ARCHIVED');
    const coveredCategories = new Set();
    policies.forEach(p => {
      (p.linkedCategories || []).forEach(cat => coveredCategories.add(cat));
    });
    return allCategories.filter(cat => !coveredCategories.has(cat));
  }

  // === COMPLIANCE MAP ===

  getComplianceMap() {
    return storage.getJSON(this._entityKey(this.COMPLIANCE_KEY), {});
  }

  getArticleStatus(articleId) {
    const map = this.getComplianceMap();
    return map[articleId] || { status: 'NOT_ASSESSED', linkedControlIds: [], notes: '' };
  }

  async updateArticleStatus(articleId, data) {
    const map = this.getComplianceMap();
    map[articleId] = { ...this.getArticleStatus(articleId), ...data, updatedAt: new Date().toISOString() };
    await this._dualWriteJson(this.COMPLIANCE_KEY, map);
    this.notifyListeners();
    auditService.log('UPDATE', 'BWRA', { entity: 'ComplianceArticle', id: articleId, action: 'update_status' });
  }

  // Gap analysis: compute traffic light status per article
  // GREEN = controls linked + status COMPLIANT
  // YELLOW = controls linked but not validated (status != COMPLIANT)
  // RED = no controls linked
  getArticleTrafficLight(article) {
    const controls = this.getLinkedControlsForArticle(article);
    const articleStatus = this.getArticleStatus(article.id);

    if (controls.length === 0) return 'RED';
    if (articleStatus.status === 'COMPLIANT') return 'GREEN';
    return 'YELLOW';
  }

  // Get full gap analysis for all articles
  getGapAnalysis(articles) {
    const results = articles.map(article => {
      const controls = this.getLinkedControlsForArticle(article);
      const trafficLight = this.getArticleTrafficLight(article);
      return { article, trafficLight, controlCount: controls.length };
    });

    const green = results.filter(r => r.trafficLight === 'GREEN').length;
    const yellow = results.filter(r => r.trafficLight === 'YELLOW').length;
    const red = results.filter(r => r.trafficLight === 'RED').length;
    const covered = green + yellow;

    return {
      results,
      summary: { green, yellow, red, covered, total: articles.length, pct: articles.length > 0 ? Math.round((covered / articles.length) * 100) : 0 },
    };
  }

  // Auto-detect which controls relate to an article based on risk categories
  getLinkedControlsForArticle(article) {
    const risks = this.getRisks();
    const linkedControls = [];
    const manualMap = this.getComplianceMap();
    const manualIds = (manualMap[article.id]?.linkedControlIds) || [];

    risks.forEach(risk => {
      // Manual controls (persisted)
      risk.controls.forEach(ctrl => {
        // Manual link
        if (manualIds.includes(ctrl.id)) {
          linkedControls.push({ ...ctrl, riskId: risk.id, riskName: risk.name, linkType: 'manual' });
          return;
        }
        // Auto-link by category match
        if (article.relatedCategories.includes(risk.category)) {
          linkedControls.push({ ...ctrl, riskId: risk.id, riskName: risk.name, linkType: 'auto' });
        }
      });

      // Auto-generated controls from GeoRisk/CustomerRisk/TxMon
      if (article.relatedCategories.includes(risk.category)) {
        const autoControls = this.getAutoControlsForRisk(risk.id);
        autoControls.forEach(ctrl => {
          linkedControls.push({ ...ctrl, riskId: risk.id, riskName: risk.name, linkType: 'auto' });
        });
      }
    });

    // Deduplicate by control id
    const seen = new Set();
    return linkedControls.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }

  // === CATEGORY ANALYSIS ===

  getCategoryAnalysis() {
    return storage.getJSON(this._entityKey(this.CATEGORY_ANALYSIS_KEY), {});
  }

  getCategoryAnalysisForCategory(categoryId) {
    const all = this.getCategoryAnalysis();
    return all[categoryId] || {
      inherentRiskLevel: '',
      trendDirection: '',
      keyFindings: '',
      mitigationStrategy: '',
      regulatoryContext: '',
      lastReviewedAt: null,
      reviewer: '',
    };
  }

  async saveCategoryAnalysis(categoryId, data) {
    const all = this.getCategoryAnalysis();
    all[categoryId] = { ...data, lastReviewedAt: new Date().toISOString() };
    await this._dualWriteJson(this.CATEGORY_ANALYSIS_KEY, all);
    this.notifyListeners();
    auditService.log('UPDATE', 'BWRA', { entity: 'CategoryAnalysis', categoryId, action: 'save_category_analysis' });
  }

  // === VERSION HISTORY ===

  getVersions() {
    return storage.getJSON(this._entityKey(this.VERSIONS_KEY), []);
  }

  getVersion(versionId) {
    const versions = this.getVersions();
    return versions.find(v => v.id === versionId) || null;
  }

  async saveVersion(label, createdBy) {
    const versions = this.getVersions();
    const versionNumber = versions.length + 1;
    const now = new Date().toISOString();

    const snapshot = {
      id: `BWRA-V${String(versionNumber).padStart(3, '0')}`,
      versionNumber,
      label: label || `v${versionNumber}`,
      createdAt: now,
      createdBy: createdBy || '',
      risks: JSON.parse(JSON.stringify(this.getRisks())),
      scope: JSON.parse(JSON.stringify(this.getScope())),
      appetite: JSON.parse(JSON.stringify(this.getRiskAppetite())),
      policies: JSON.parse(JSON.stringify(this.getPolicies())),
      compliance: JSON.parse(JSON.stringify(this.getComplianceMap())),
      summary: this._buildVersionSummary(),
    };

    versions.push(snapshot);
    await this._dualWriteJson(this.VERSIONS_KEY, versions);
    this.notifyListeners();
    auditService.log('CREATE', 'BWRA', { entity: 'Version', name: snapshot.label, id: snapshot.id });
    return snapshot;
  }

  async deleteVersion(versionId) {
    const versions = this.getVersions();
    const version = versions.find(v => v.id === versionId);
    const filtered = versions.filter(v => v.id !== versionId);
    await this._dualWriteJson(this.VERSIONS_KEY, filtered);
    this.notifyListeners();
    if (version) {
      auditService.log('DELETE', 'BWRA', { entity: 'Version', name: version.label, id: versionId });
    }
    return filtered;
  }

  _buildVersionSummary() {
    const risks = this.getRisks();
    const totalControls = risks.reduce((sum, r) => sum + (r.controls ? r.controls.length : 0), 0);
    const mitigated = risks.filter(r => r.status === 'MITIGATED').length;
    const policies = this.getPolicies();

    let avgGross = 0;
    let avgResidual = 0;
    if (risks.length > 0) {
      avgGross = risks.reduce((sum, r) => {
        const g = r.grossRisk || {};
        return sum + calculateRiskScore(g.likelihood || 0, g.impact || 0);
      }, 0) / risks.length;
      avgResidual = risks.reduce((sum, r) => {
        const res = r.residualRisk || r.grossRisk || {};
        return sum + calculateRiskScore(res.likelihood || 0, res.impact || 0);
      }, 0) / risks.length;
    }

    return {
      riskCount: risks.length,
      controlCount: totalControls,
      mitigatedCount: mitigated,
      policyCount: policies.length,
      avgGrossRisk: Math.round(avgGross * 10) / 10,
      avgResidualRisk: Math.round(avgResidual * 10) / 10,
    };
  }

  compareVersions(v1Id, v2Id) {
    const v1 = this.getVersion(v1Id);
    const v2 = this.getVersion(v2Id);
    if (!v1 || !v2) return null;

    const changes = [];

    // Compare summary statistics
    if (v1.summary.riskCount !== v2.summary.riskCount) {
      changes.push({ type: 'risks', field: 'count', from: v1.summary.riskCount, to: v2.summary.riskCount });
    }
    if (v1.summary.controlCount !== v2.summary.controlCount) {
      changes.push({ type: 'controls', field: 'count', from: v1.summary.controlCount, to: v2.summary.controlCount });
    }
    if (v1.summary.policyCount !== v2.summary.policyCount) {
      changes.push({ type: 'policies', field: 'count', from: v1.summary.policyCount, to: v2.summary.policyCount });
    }
    if (v1.summary.avgGrossRisk !== v2.summary.avgGrossRisk) {
      changes.push({ type: 'risk', field: 'avgGross', from: v1.summary.avgGrossRisk, to: v2.summary.avgGrossRisk });
    }
    if (v1.summary.avgResidualRisk !== v2.summary.avgResidualRisk) {
      changes.push({ type: 'risk', field: 'avgResidual', from: v1.summary.avgResidualRisk, to: v2.summary.avgResidualRisk });
    }

    // Detect added/removed risks
    const v1RisksMap = new Map((v1.risks || []).map(r => [r.id, r]));
    const v2RisksMap = new Map((v2.risks || []).map(r => [r.id, r]));
    for (const [id, r] of v2RisksMap) {
      if (!v1RisksMap.has(id)) changes.push({ type: 'risk', field: 'added', name: r.name, id: r.id });
    }
    for (const [id, r] of v1RisksMap) {
      if (!v2RisksMap.has(id)) changes.push({ type: 'risk', field: 'removed', name: r.name, id: r.id });
    }

    // Detect changes within existing risks
    for (const [id, r1] of v1RisksMap) {
      const r2 = v2RisksMap.get(id);
      if (!r2) continue;

      const ctx = { riskId: r1.id, riskName: r1.name };

      if (r1.name !== r2.name)
        changes.push({ type: 'risk', field: 'name', ...ctx, from: r1.name, to: r2.name });
      if (r1.category !== r2.category)
        changes.push({ type: 'risk', field: 'category', ...ctx, from: r1.category, to: r2.category });
      if (r1.status !== r2.status)
        changes.push({ type: 'risk', field: 'status', ...ctx, from: r1.status, to: r2.status });
      if ((r1.description || '') !== (r2.description || ''))
        changes.push({ type: 'risk', field: 'description', ...ctx, from: r1.description || '', to: r2.description || '' });

      const g1 = r1.grossRisk || {}, g2 = r2.grossRisk || {};
      if ((g1.likelihood || 0) !== (g2.likelihood || 0) || (g1.impact || 0) !== (g2.impact || 0))
        changes.push({ type: 'risk', field: 'grossRisk', ...ctx,
          from: { likelihood: g1.likelihood || 0, impact: g1.impact || 0 },
          to: { likelihood: g2.likelihood || 0, impact: g2.impact || 0 } });

      const res1 = r1.residualRisk || {}, res2 = r2.residualRisk || {};
      if ((res1.likelihood || 0) !== (res2.likelihood || 0) || (res1.impact || 0) !== (res2.impact || 0))
        changes.push({ type: 'risk', field: 'residualRisk', ...ctx,
          from: { likelihood: res1.likelihood || 0, impact: res1.impact || 0 },
          to: { likelihood: res2.likelihood || 0, impact: res2.impact || 0 } });

      // Controls within this risk
      const c1Map = new Map((r1.controls || []).map(c => [c.id, c]));
      const c2Map = new Map((r2.controls || []).map(c => [c.id, c]));
      for (const [cId, c2] of c2Map) {
        if (!c1Map.has(cId)) changes.push({ type: 'control', field: 'added', ...ctx, controlId: cId, controlName: c2.name });
      }
      for (const [cId, c1] of c1Map) {
        if (!c2Map.has(cId)) changes.push({ type: 'control', field: 'removed', ...ctx, controlId: cId, controlName: c1.name });
      }
      for (const [cId, c1] of c1Map) {
        const c2 = c2Map.get(cId);
        if (!c2) continue;
        const cCtx = { ...ctx, controlId: cId, controlName: c1.name };
        if (c1.effectiveness !== c2.effectiveness)
          changes.push({ type: 'control', field: 'effectiveness', ...cCtx, from: c1.effectiveness, to: c2.effectiveness });
        if (c1.name !== c2.name)
          changes.push({ type: 'control', field: 'name', ...cCtx, from: c1.name, to: c2.name });
        if (c1.type !== c2.type)
          changes.push({ type: 'control', field: 'type', ...cCtx, from: c1.type, to: c2.type });
      }
    }

    // Detect added/removed policies
    const v1PoliciesMap = new Map((v1.policies || []).map(p => [p.id, p]));
    const v2PoliciesMap = new Map((v2.policies || []).map(p => [p.id, p]));
    for (const [id, p] of v2PoliciesMap) {
      if (!v1PoliciesMap.has(id)) changes.push({ type: 'policy', field: 'added', name: p.name, id: p.id });
    }
    for (const [id, p] of v1PoliciesMap) {
      if (!v2PoliciesMap.has(id)) changes.push({ type: 'policy', field: 'removed', name: p.name, id: p.id });
    }

    // Detect changes within existing policies
    for (const [id, p1] of v1PoliciesMap) {
      const p2 = v2PoliciesMap.get(id);
      if (!p2) continue;
      const pCtx = { policyId: p1.id, policyName: p1.name };
      if (p1.name !== p2.name)
        changes.push({ type: 'policy', field: 'name', ...pCtx, from: p1.name, to: p2.name });
      if (p1.status !== p2.status)
        changes.push({ type: 'policy', field: 'status', ...pCtx, from: p1.status, to: p2.status });
      if ((p1.description || '') !== (p2.description || ''))
        changes.push({ type: 'policy', field: 'description', ...pCtx, from: p1.description || '', to: p2.description || '' });
    }

    // Compare scope
    const sc1 = v1.scope || {}, sc2 = v2.scope || {};
    if ((sc1.businessDescription || '') !== (sc2.businessDescription || ''))
      changes.push({ type: 'scope', field: 'businessDescription', from: sc1.businessDescription || '', to: sc2.businessDescription || '' });
    if (JSON.stringify(sc1.products || []) !== JSON.stringify(sc2.products || []))
      changes.push({ type: 'scope', field: 'products', from: sc1.products || [], to: sc2.products || [] });
    if (JSON.stringify(sc1.customerCategories || []) !== JSON.stringify(sc2.customerCategories || []))
      changes.push({ type: 'scope', field: 'customerCategories', from: sc1.customerCategories || [], to: sc2.customerCategories || [] });
    if (JSON.stringify(sc1.distributionChannels || []) !== JSON.stringify(sc2.distributionChannels || []))
      changes.push({ type: 'scope', field: 'distributionChannels', from: sc1.distributionChannels || [], to: sc2.distributionChannels || [] });

    // Compare risk appetite
    const ap1 = v1.appetite || {}, ap2 = v2.appetite || {};
    if ((ap1.statement || '') !== (ap2.statement || ''))
      changes.push({ type: 'appetite', field: 'statement', from: ap1.statement || '', to: ap2.statement || '' });
    if (JSON.stringify(ap1.thresholds || {}) !== JSON.stringify(ap2.thresholds || {}))
      changes.push({ type: 'appetite', field: 'thresholds', from: ap1.thresholds || {}, to: ap2.thresholds || {} });

    // Compare compliance map
    const comp1 = v1.compliance || {}, comp2 = v2.compliance || {};
    const allArticleIds = new Set([...Object.keys(comp1), ...Object.keys(comp2)]);
    for (const articleId of allArticleIds) {
      const a1 = comp1[articleId] || {}, a2 = comp2[articleId] || {};
      if ((a1.status || '') !== (a2.status || ''))
        changes.push({ type: 'compliance', field: 'status', articleId, from: a1.status || '', to: a2.status || '' });
      if ((a1.notes || '') !== (a2.notes || ''))
        changes.push({ type: 'compliance', field: 'notes', articleId, from: a1.notes || '', to: a2.notes || '' });
    }

    return { v1: v1.label, v2: v2.label, changes };
  }

  // === ANNUAL REVIEWS (ÅRKU) ===

  getAnnualReviews() {
    return storage.getJSON(this._entityKey(this.ANNUAL_REVIEWS_KEY), []);
  }

  async _saveAnnualReviews(reviews) {
    await this._dualWriteJson(this.ANNUAL_REVIEWS_KEY, reviews);
    this.notifyListeners();
  }

  async addAnnualReview(review) {
    const reviews = this.getAnnualReviews();
    reviews.push(review);
    await this._saveAnnualReviews(reviews);
    auditService.log('CREATE', 'BWRA', { entity: 'AnnualReview', id: review.id, period: review.period });
    return reviews;
  }

  async updateAnnualReview(reviewId, updatedReview) {
    const reviews = this.getAnnualReviews();
    const index = reviews.findIndex(r => r.id === reviewId);
    if (index >= 0) {
      reviews[index] = updatedReview;
      await this._saveAnnualReviews(reviews);
      auditService.log('UPDATE', 'BWRA', { entity: 'AnnualReview', id: reviewId, period: updatedReview.period });
    }
    return reviews;
  }

  async deleteAnnualReview(reviewId) {
    const reviews = this.getAnnualReviews();
    const review = reviews.find(r => r.id === reviewId);
    const filtered = reviews.filter(r => r.id !== reviewId);
    await this._saveAnnualReviews(filtered);
    if (review) {
      auditService.log('DELETE', 'BWRA', { entity: 'AnnualReview', id: reviewId, period: review.period });
    }
    return filtered;
  }

  generateAnnualReviewId() {
    const reviews = this.getAnnualReviews();
    const maxNum = reviews.reduce((max, r) => {
      const num = parseInt(r.id.replace('ARKU-', ''), 10);
      return isNaN(num) ? max : (num > max ? num : max);
    }, 0);
    return `ARKU-${String(maxNum + 1).padStart(3, '0')}`;
  }

  // === RISK TIMELINE ===

  // Returns chronological lifecycle events for a single risk:
  // identification, actions created/completed, control evaluations from annual reviews.
  getRiskTimeline(riskId) {
    const risks = this.getRisks();
    const risk = risks.find(r => r.id === riskId);
    if (!risk) return [];

    const events = [];

    // 1. Risk identification event
    events.push({
      id: `${riskId}-identified`,
      type: 'RISK_IDENTIFIED',
      date: risk.createdAt || null,
      riskId,
      riskName: risk.name,
      status: risk.status,
      grossRisk: risk.grossRisk,
      residualRisk: risk.residualRisk,
    });

    // 2. Actions for this risk
    const actions = risk.actions || [];
    actions.forEach(action => {
      if (action.createdAt) {
        events.push({
          id: `${action.id}-created`,
          type: 'ACTION_CREATED',
          date: action.createdAt,
          riskId,
          actionId: action.id,
          description: action.description,
          priority: action.priority,
          responsible: action.responsible,
          deadline: action.deadline,
          status: action.status,
        });
      }
      if (action.status === 'COMPLETED' || action.status === 'VERIFIED') {
        events.push({
          id: `${action.id}-${action.status.toLowerCase()}`,
          type: action.status === 'VERIFIED' ? 'ACTION_VERIFIED' : 'ACTION_COMPLETED',
          date: action.completedAt || action.deadline || null,
          riskId,
          actionId: action.id,
          description: action.description,
          status: action.status,
        });
      }
    });

    // 3. Control evaluations from annual reviews
    const annualReviews = this.getAnnualReviews();
    annualReviews.forEach(review => {
      const evals = (review.controlEvaluations || []).filter(e => e.riskId === riskId && e.conducted !== null);
      evals.forEach(evaluation => {
        events.push({
          id: `${review.id}-${evaluation.controlId}`,
          type: 'CONTROL_EVALUATED',
          date: review.evaluationDate,
          riskId,
          reviewId: review.id,
          reviewPeriod: review.period,
          controlId: evaluation.controlId,
          controlName: evaluation.controlName,
          effectiveness: evaluation.effectiveness,
          conducted: evaluation.conducted,
          observations: evaluation.observations,
          evaluatedBy: review.evaluatedBy,
        });
      });
    });

    // Sort chronologically (events without date go first as "earliest known")
    events.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return -1;
      if (!b.date) return 1;
      return new Date(a.date) - new Date(b.date);
    });

    return events;
  }

  // === SCOPE ↔ RISK LINKS ===
  // Links are stored as { [riskId]: string[] } where each string is a scope item key.
  // Scope item key format: "{field}:{groupIndex}" for a group, "{field}:{groupIndex}:{childIndex}" for a child.

  getScopeRiskLinks() {
    return storage.getJSON(this._entityKey(this.SCOPE_RISK_LINKS_KEY), {});
  }

  async _saveScopeRiskLinks(links) {
    await this._dualWriteJson(this.SCOPE_RISK_LINKS_KEY, links);
    this.notifyListeners();
  }

  // Returns all scope item keys linked to a risk
  getScopeKeysForRisk(riskId) {
    const links = this.getScopeRiskLinks();
    return links[riskId] || [];
  }

  // Returns all risk IDs linked to a scope item key
  getRiskIdsForScopeKey(scopeKey) {
    const links = this.getScopeRiskLinks();
    const riskIds = [];
    for (const [riskId, keys] of Object.entries(links)) {
      if ((keys || []).includes(scopeKey)) riskIds.push(riskId);
    }
    return riskIds;
  }

  async addScopeRiskLink(riskId, scopeKey) {
    const links = this.getScopeRiskLinks();
    if (!links[riskId]) links[riskId] = [];
    if (!links[riskId].includes(scopeKey)) {
      links[riskId] = [...links[riskId], scopeKey];
      await this._saveScopeRiskLinks(links);
      auditService.log('CREATE', 'BWRA', { entity: 'ScopeRiskLink', riskId, scopeKey });
    }
  }

  async removeScopeRiskLink(riskId, scopeKey) {
    const links = this.getScopeRiskLinks();
    if (links[riskId]) {
      links[riskId] = links[riskId].filter(k => k !== scopeKey);
      if (links[riskId].length === 0) delete links[riskId];
      await this._saveScopeRiskLinks(links);
      auditService.log('DELETE', 'BWRA', { entity: 'ScopeRiskLink', riskId, scopeKey });
    }
  }

  async setScopeRiskLinksForKey(scopeKey, riskIds) {
    const links = this.getScopeRiskLinks();
    // Remove this scopeKey from all risks
    for (const rId of Object.keys(links)) {
      links[rId] = (links[rId] || []).filter(k => k !== scopeKey);
      if (links[rId].length === 0) delete links[rId];
    }
    // Add scopeKey to the selected riskIds
    for (const rId of riskIds) {
      if (!links[rId]) links[rId] = [];
      if (!links[rId].includes(scopeKey)) links[rId].push(scopeKey);
    }
    await this._saveScopeRiskLinks(links);
    auditService.log('UPDATE', 'BWRA', { entity: 'ScopeRiskLink', scopeKey, riskCount: riskIds.length });
  }

  // Parses a scope item key back to a human-readable label given scope data
  getScopeItemLabel(scopeKey, scope) {
    const parts = scopeKey.split(':');
    const field = parts[0];
    const groupIndex = parseInt(parts[1], 10);
    const childIndex = parts.length > 2 ? parseInt(parts[2], 10) : null;
    const list = scope[field] || [];
    const group = list[groupIndex];
    if (!group) return scopeKey;
    if (childIndex === null) return group.name || scopeKey;
    const children = group.children || [];
    return `${group.name ? group.name + ' › ' : ''}${children[childIndex] || scopeKey}`;
  }

  // Returns field display name for a scope key's field
  getScopeFieldName(scopeKey) {
    const field = scopeKey.split(':')[0];
    const names = {
      products: 'products',
      customerCategories: 'customerCategories',
      distributionChannels: 'distributionChannels',
    };
    return names[field] || field;
  }

  // === RISK ACCEPTANCE ===

  getRiskAcceptances() {
    return storage.getJSON(this._entityKey(this.RISK_ACCEPTANCES_KEY), {});
  }

  getRiskAcceptance(riskId) {
    const all = this.getRiskAcceptances();
    return all[riskId] || null;
  }

  async acceptRisk(riskId, { motivation, acceptedBy, acceptedAt }) {
    const all = this.getRiskAcceptances();
    all[riskId] = {
      riskId,
      motivation,
      acceptedBy,
      acceptedAt: acceptedAt || new Date().toISOString(),
    };
    await this._dualWriteJson(this.RISK_ACCEPTANCES_KEY, all);
    this.notifyListeners();
    auditService.log('UPDATE', 'BWRA', { entity: 'Risk', id: riskId, action: 'accept_risk', acceptedBy });
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('approvalGranted', { type: 'riskAcceptance', riskId, acceptedBy });
    } catch (_e) { /* best-effort */ }
  }

  async revokeRiskAcceptance(riskId) {
    const all = this.getRiskAcceptances();
    if (all[riskId]) {
      delete all[riskId];
      await this._dualWriteJson(this.RISK_ACCEPTANCES_KEY, all);
      this.notifyListeners();
      auditService.log('UPDATE', 'BWRA', { entity: 'Risk', id: riskId, action: 'revoke_acceptance' });
    }
  }

  // Returns list of {riskId, risk, acceptance, daysLeft, isExpired} for acceptances
  // expiring within daysThreshold days. Expired ones (daysLeft <= 0) are always included.
  getExpiringAcceptances(daysThreshold = 30) {
    const acceptances = this.getRiskAcceptances();
    const risks = this.getRisks();
    const now = new Date();
    const results = [];

    for (const [riskId, acceptance] of Object.entries(acceptances)) {
      if (!acceptance.expiresAt) continue;
      const expiresAt = new Date(acceptance.expiresAt);
      const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      const isExpired = daysLeft <= 0;

      if (daysLeft <= daysThreshold) {
        const risk = risks.find(r => r.id === riskId);
        if (risk && risk.status !== 'ARCHIVED') {
          results.push({ riskId, risk, acceptance, daysLeft, isExpired });
        }
      }
    }

    // Sort: expired first, then by fewest days left
    return results.sort((a, b) => a.daysLeft - b.daysLeft);
  }

  // Marks expired acceptances as requiring reassessment. Call on startup or periodically.
  // Returns number of acceptances updated.
  async processExpiredAcceptances() {
    const acceptances = this.getRiskAcceptances();
    const now = new Date();
    let updated = 0;

    for (const [riskId, acceptance] of Object.entries(acceptances)) {
      if (!acceptance.expiresAt) continue;
      const expiresAt = new Date(acceptance.expiresAt);
      if (expiresAt <= now && !acceptance.reassessmentRequired) {
        acceptances[riskId] = { ...acceptance, reassessmentRequired: true };
        updated++;
      }
    }

    if (updated > 0) {
      await this._dualWriteJson(this.RISK_ACCEPTANCES_KEY, acceptances);
      this.notifyListeners();
      auditService.log('UPDATE', 'BWRA', { entity: 'RiskAcceptance', action: 'auto_expire', count: updated });
    }

    return updated;
  }

  // === RISK SCORE HISTORY ===

  getRiskScoreHistory(riskId) {
    const all = storage.getJSON(this._entityKey(this.RISK_SCORE_HISTORY_KEY), {});
    return all[riskId] || [];
  }

  async saveRiskScoreSnapshot(riskId, grossScore, residualScore) {
    const all = storage.getJSON(this._entityKey(this.RISK_SCORE_HISTORY_KEY), {});
    const history = all[riskId] || [];
    const now = Date.now();
    const cutoff = now - 12 * 30 * 24 * 60 * 60 * 1000; // 12 months
    const pruned = history.filter(s => s.ts >= cutoff);
    pruned.push({ ts: now, grossScore, residualScore });
    all[riskId] = pruned;
    await this._dualWriteJson(this.RISK_SCORE_HISTORY_KEY, all);
  }

  seedDemoRiskScoreHistory(risks) {
    const key = this._entityKey(this.RISK_SCORE_HISTORY_KEY);
    const existing = storage.getJSON(key, null);
    if (existing !== null) return; // Already seeded
    const now = Date.now();
    const all = {};
    risks.forEach(risk => {
      const grossScore = calculateRiskScore(risk.grossRisk.likelihood, risk.grossRisk.impact);
      const residualScore = calculateRiskScore(risk.residualRisk.likelihood, risk.residualRisk.impact);
      const history = [];
      for (let i = 11; i >= 0; i--) {
        const ts = now - i * 30 * 24 * 60 * 60 * 1000;
        const variation = (Math.sin(i * 0.8 + risk.id.charCodeAt(1)) * 2) | 0;
        const residualVariation = (Math.cos(i * 0.6 + risk.id.charCodeAt(1)) * 1.5) | 0;
        history.push({
          ts,
          grossScore: Math.max(1, Math.min(25, grossScore + variation)),
          residualScore: Math.max(1, Math.min(grossScore, residualScore + residualVariation)),
        });
      }
      all[risk.id] = history;
    });
    storage.setJSON(key, all);
  }

  // === GAP ANALYSIS REPORT ===

  // Returns a gap analysis report: risks without adequate controls.
  // A risk is "in gap" if:
  //   - It has NO controls at all (manual + auto-generated), OR
  //   - ALL its combined controls have effectiveness <= 2 (ineffective)
  // Results are sorted by residual risk score descending.
  getGapAnalysisReport() {
    const risks = this.getRisks().filter(r => r.status !== 'ARCHIVED');

    const categorized = risks.map(risk => {
      const manualControls = risk.controls || [];
      const autoControls = this.getAutoControlsForRisk(risk.id);
      const allControls = [
        ...manualControls,
        ...autoControls.filter(ac =>
          !manualControls.some(mc => mc.linkedModule === ac.linkedModule)
        ),
      ];

      const grossScore = calculateRiskScore(risk.grossRisk.likelihood, risk.grossRisk.impact);
      const residualScore = calculateRiskScore(risk.residualRisk.likelihood, risk.residualRisk.impact);

      if (allControls.length === 0) {
        return {
          risk,
          gapType: 'NO_CONTROLS',
          allControls,
          grossScore,
          residualScore,
          recommendations: this._buildGapRecommendations(risk, 'NO_CONTROLS', allControls),
        };
      }

      const maxEffectiveness = Math.max(...allControls.map(c => c.effectiveness || 1));
      if (maxEffectiveness <= 2) {
        return {
          risk,
          gapType: 'INEFFECTIVE_ONLY',
          allControls,
          grossScore,
          residualScore,
          recommendations: this._buildGapRecommendations(risk, 'INEFFECTIVE_ONLY', allControls),
        };
      }

      return null; // Adequately controlled
    }).filter(Boolean);

    // Sort by residual risk score descending (highest exposure first)
    categorized.sort((a, b) => b.residualScore - a.residualScore);

    const noControls = categorized.filter(r => r.gapType === 'NO_CONTROLS');
    const ineffectiveOnly = categorized.filter(r => r.gapType === 'INEFFECTIVE_ONLY');

    return {
      generatedAt: new Date().toISOString(),
      totalRisks: risks.length,
      gapRisks: categorized,
      noControls,
      ineffectiveOnly,
      summary: {
        totalGaps: categorized.length,
        noControlsCount: noControls.length,
        ineffectiveOnlyCount: ineffectiveOnly.length,
        gapPercent: risks.length > 0 ? Math.round((categorized.length / risks.length) * 100) : 0,
      },
    };
  }

  _buildGapRecommendations(risk, gapType, allControls) {
    const recs = [];

    const categoryModuleMap = {
      GEO: 'GeoRisk',
      CUST: 'CustomerRisk',
      TX: 'TransactionMonitoring',
      CHAN: 'TransactionMonitoring',
    };

    const linkedModule = categoryModuleMap[risk.category];

    if (gapType === 'NO_CONTROLS') {
      if (linkedModule) {
        recs.push({ type: 'LINK_MODEL', module: linkedModule });
      } else {
        recs.push({ type: 'ADD_MANUAL_CONTROL' });
      }
      recs.push({ type: 'ADD_ACTION' });
    } else if (gapType === 'INEFFECTIVE_ONLY') {
      const hasModelControls = allControls.some(c => c.type === 'MODEL' || c.linkedModule);
      if (hasModelControls) {
        recs.push({ type: 'IMPROVE_MODEL_VALIDATION' });
      }
      recs.push({ type: 'INCREASE_EFFECTIVENESS' });
      if (linkedModule) {
        recs.push({ type: 'VERIFY_MODEL_COVERAGE', module: linkedModule });
      }
    }

    return recs;
  }

  // === BOARD REPORT ===

  getBoardReportData() {
    const allRisks = this.getRisks();
    const activeRisks = allRisks.filter(r => r.status !== 'ARCHIVED');
    const appetite = this.getRiskAppetite();

    const grossScores = activeRisks.map(r => calculateRiskScore(r.grossRisk.likelihood, r.grossRisk.impact));
    const residualScores = activeRisks.map(r => calculateRiskScore(r.residualRisk.likelihood, r.residualRisk.impact));
    const avgGross = grossScores.length > 0 ? Math.round((grossScores.reduce((a, b) => a + b, 0) / grossScores.length) * 10) / 10 : 0;
    const avgResidual = residualScores.length > 0 ? Math.round((residualScores.reduce((a, b) => a + b, 0) / residualScores.length) * 10) / 10 : 0;

    // Top 10 risks by residual score descending
    const top10 = activeRisks
      .map(r => ({
        ...r,
        grossScore: calculateRiskScore(r.grossRisk.likelihood, r.grossRisk.impact),
        residualScore: calculateRiskScore(r.residualRisk.likelihood, r.residualRisk.impact),
      }))
      .sort((a, b) => b.residualScore - a.residualScore)
      .slice(0, 10);

    // Controls stats
    const allControls = activeRisks.flatMap(r => r.controls || []);
    const effectiveControls = allControls.filter(c => (c.effectiveness || 1) >= 3);
    const risksWithControls = activeRisks.filter(r => (r.controls || []).length > 0);
    const avgEff = allControls.length > 0
      ? Math.round((allControls.reduce((sum, c) => sum + (c.effectiveness || 1), 0) / allControls.length) * 10) / 10
      : 0;

    // Gap analysis summary
    const gapReport = this.getGapAnalysisReport();

    return {
      generatedAt: new Date().toISOString(),
      totalRisks: allRisks.length,
      activeRisks: activeRisks.length,
      mitigated: activeRisks.filter(r => r.status === 'MITIGATED').length,
      assessed: activeRisks.filter(r => r.status === 'ASSESSED').length,
      identified: activeRisks.filter(r => r.status === 'IDENTIFIED').length,
      avgGross,
      avgResidual,
      riskReduction: avgGross > 0 ? Math.round((1 - avgResidual / avgGross) * 100) : 0,
      top10,
      controlStats: {
        total: allControls.length,
        effective: effectiveControls.length,
        coverage: activeRisks.length > 0 ? Math.round((risksWithControls.length / activeRisks.length) * 100) : 0,
        avgEffectiveness: avgEff,
        risksWithoutControls: activeRisks.length - risksWithControls.length,
      },
      gapSummary: {
        totalGaps: gapReport.summary.totalGaps,
        noControlsCount: gapReport.summary.noControlsCount,
        ineffectiveOnlyCount: gapReport.summary.ineffectiveOnlyCount,
        gapPercent: gapReport.summary.gapPercent,
      },
      appetiteStatement: appetite.statement || '',
    };
  }

  // === CONTROL TEST RESULTS ===

  getControlTestResults(controlId) {
    const all = storage.getJSON(this._entityKey(this.CONTROL_TEST_RESULTS_KEY), {});
    return all[controlId] || [];
  }

  getAllControlTestResultsSummary() {
    return storage.getJSON(this._entityKey(this.CONTROL_TEST_RESULTS_KEY), {});
  }

  async importControlTestResults(controlId, results, append = true) {
    const all = storage.getJSON(this._entityKey(this.CONTROL_TEST_RESULTS_KEY), {});
    const existing = all[controlId] || [];
    const importedAt = new Date().toISOString();
    const newEntries = results.map(r => ({ ...r, importedAt }));
    all[controlId] = append ? [...existing, ...newEntries] : newEntries;
    await this._dualWriteJson(this.CONTROL_TEST_RESULTS_KEY, all);
    this.notifyListeners();
    auditService.log('CREATE', 'BWRA', { entity: 'ControlTestResults', controlId, count: results.length, append });
  }

  async clearControlTestResults(controlId) {
    const all = storage.getJSON(this._entityKey(this.CONTROL_TEST_RESULTS_KEY), {});
    delete all[controlId];
    await this._dualWriteJson(this.CONTROL_TEST_RESULTS_KEY, all);
    this.notifyListeners();
    auditService.log('DELETE', 'BWRA', { entity: 'ControlTestResults', controlId });
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
}

const bwraService = new BWRAService();

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.bwraService = bwraService;
}

export default bwraService;
